'use client'

import Image from "next/image"
import { Button } from "@/components/ui/button"
import { useDemoModal } from "@/components/demo-modal-provider"
import { useState, useEffect } from "react"
import { Briefcase, CheckSquare, Megaphone, Palette, Users, Code, Server, Cog, Package, FileText, ShieldCheck, Pill, Scale, MessageCircle, PieChart, Smartphone } from "lucide-react"

// Define 9 work types mapped to available screenshots and routes
const workTypes = [
  { id: 'projects', label: 'Claims', image: '/images/dami/claims.png', route: '/project', icon: FileText },

  { id: 'product', label: 'Mobile App', image: '/images/dami/mobile-app.png', route: '/product', icon: Smartphone },

  { id: 'marketing', label: 'Authorization', image: '/images/dami/authorization.png', route: '/marketing', icon: ShieldCheck },

  { id: 'design', label: 'Providers', image: '/images/dami/providers.png', route: '/design', icon: Briefcase },

  { id: 'it', label: 'CRM', image: '/images/dami/crm.png', route: '/it', icon: MessageCircle },

  { id: 'software', label: 'Underwriting', image: '/images/dami/underwriting.png', route: '/software', icon: Scale },

  { id: 'fintech', label: 'Payment', image: '/images/dami/payment.png', route: '/payment', icon: Cog },

  { id: 'medicine', label: 'Pharmacy', image: '/images/dami/pharmacy.png', route: '/medicine', icon: Pill },

  { id: 'sales', label: 'Sales', image: '/images/dami/sales.png', route: '/sales', icon: Package }
]

// 4 gradient themes to cycle on selection
const buttonColors = [
  'bg-gradient-to-r from-[#1a85ca] via-blue-500 to-[#2cd0ba] text-white hover:brightness-110',
  'bg-gradient-to-r from-[#2cd0ba] via-teal-500 to-cyan-600 text-white hover:brightness-110',
  'bg-gradient-to-r from-[#e22951] via-fuchsia-500 to-pink-500 text-white hover:brightness-110',
  'bg-gradient-to-r from-[#edbe3c] via-amber-500 to-red-500 text-white hover:brightness-110'
]

export function HeroSection() {
  const { openDemoModal } = useDemoModal()
  // No default selection per requirements; support multi-select
  const [hoveredWork, setHoveredWork] = useState<string | null>(null)
  const [clickedWork, setClickedWork] = useState<string | null>(null)
  const [currentMobileImage, setCurrentMobileImage] = useState(0)

  // Mobile auto-slide animation
  useEffect(() => {
    const isMobile = window.innerWidth < 768
    if (!isMobile) return

    const interval = setInterval(() => {
      setCurrentMobileImage(prev => (prev + 1) % workTypes.length)
    }, 3000) // Change image every 3 seconds

    return () => clearInterval(interval)
  }, [])

  const getButtonColor = () => {
    const activeWork = clickedWork || hoveredWork
    if (activeWork) {
      const index = workTypes.findIndex(w => w.id === activeWork)
      if (index !== -1) {
        return buttonColors[index % buttonColors.length]
      }
    }
    return buttonColors[0]
  }

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-[#2cd0ba]/10 overflow-hidden mb-[6vh] px-[5%] py-[5%]">
      {/* Header content – moved above background per request */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-gray-900">
            Run a Smarter, AI-Powered HMO with OctoHealth ERP
            <br />
            <span className="text-gray-600"></span>
          </h1>
          <p className="mt-6 text-[0.9rem] sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Transform your operations with one intelligent platform that automates underwriting, claims, authorizations, and pharmacy benefits in real time
          </p>

          <Button
            size="lg"
            className={`mt-8 px-3 py-2.5 rounded-full text-base font-medium shadow-lg hover:shadow-xl transition-all ${getButtonColor()}`}
            onClick={openDemoModal}
          >
            Request a Demo →
          </Button>

          {/* <p className="mt-3 text-sm text-gray-500">
            No credit card needed • Unlimited time on Free plan
          </p> */}
        </div>
      </div>

      {/* Center background: widened by 15% (height fixed) below the header */}
      <div className="relative flex justify-center">
        <div className="relative mt-6 sm:mt-8 md:mt-10 h-[336px] sm:h-[432px] md:h-[552px] lg:h-[624px] w-[540px] sm:w-[700px] md:w-[900px] lg:w-[1024px] rounded-2xl overflow-hidden ">
          {workTypes.map((work, index) => {
            // Mobile behavior: auto-slide and unblur
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
            const isCurrentMobileImage = isMobile && index === currentMobileImage
            const shouldShowOnMobile = isMobile && isCurrentMobileImage
            
            // Desktop behavior: hover or click to unblur
            const activeWork = clickedWork || hoveredWork
            const shouldShowOnDesktop = !isMobile && (
              activeWork
                ? activeWork === work.id
                : false
            )

            return (
              <div
                key={work.id}
                className={`absolute inset-0 transition-all duration-700 bg-white ${
                  isMobile
                    ? shouldShowOnMobile
                      ? 'opacity-100 blur-none z-10 transform translate-x-0'
                      : index < currentMobileImage
                        ? 'opacity-0 blur-none pointer-events-none transform -translate-x-full'
                        : 'opacity-0 blur-none pointer-events-none transform translate-x-full'
                    : activeWork
                      ? shouldShowOnDesktop
                        ? 'opacity-100 blur-none z-10'
                        : 'opacity-0 blur-none pointer-events-none'
                      : index === 0
                        ? 'opacity-100 blur-[2px] z-10'
                        : 'opacity-0 blur-none pointer-events-none'
                }`}
              >
                {/* Background Blur Layer */}
                {/* <div className="absolute inset-0 z-0 overflow-hidden">
                  <Image
                    src={work.image}
                    alt=""
                    fill
                    sizes="(max-width: 640px) 540px, (max-width: 768px) 700px, (max-width: 1024px) 900px, 1024px"
                    className="object-cover opacity-20 blur-xl scale-110"
                  />
                </div> */}
                
                {/* Foreground Content Layer */}
                <div className="relative h-full w-full z-10">
                  <Image
                    src={work.image}
                    alt={work.label}
                    fill
                    sizes="(max-width: 640px) 540px, (max-width: 768px) 700px, (max-width: 1024px) 900px, 1024px"
                    className="object-contain object-center"
                    priority={work.id === 'projects'}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Overlay card: hidden on mobile, visible on desktop */}
      <div 
        className="hidden md:block absolute right-6 md:right-10 top-[39%] translate-x-[-20%] translate-y-0 z-20 w-[280px] md:w-[320px]"
        onMouseLeave={() => {
          setHoveredWork(null)
          setClickedWork(null)
        }}
      >
        <div className="bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/60">
          <h3 className="text-lg font-medium text-gray-900 mb-4">What Feature Interest You?</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {workTypes.map((work) => {
              const isActive = (clickedWork || hoveredWork) === work.id;
              return (
                <div
                  key={work.id}
                  className={`w-[calc(33.333%-0.34rem)] aspect-square p-2 flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-200 text-xs font-medium cursor-pointer ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white/80 text-gray-700'
                  }`}
                  onMouseEnter={() => setHoveredWork(work.id)}
                  onMouseLeave={() => setHoveredWork(null)}
                  onClick={() => setClickedWork(work.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setClickedWork(work.id)
                    }
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-1 text-center px-1">
                    {work.icon && (
                      <work.icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                    )}
                    <span className="truncate text-[11px] sm:text-xs">{work.label}</span>
                  </div>
                </div>
              )
            })}
          </div>

          <Button
            className={`w-full mt-4 rounded-full px-3 py-2.5 text-base font-medium shadow-lg hover:shadow-xl transition-all ${getButtonColor()}`}
            onClick={() => {
              document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            Learn More
          </Button>
        </div>
      </div>
    </section>
  )
}
